import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ManagementPageLayout from '../../components/layout/ManagementPageLayout';
import FolderTabs from '../../components/common/FolderTabs';
import ManagementPageSkeleton, { ManagementContentSkeleton } from '../../components/layout/ManagementPageSkeleton';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ServiceCard from '../../components/services/ServiceCard';
import ServiceEditorDialog from '../../components/services/ServiceEditorDialog';
import { allowsOnline } from '../../constants/CustomerPaymentMode';
import ServiceCategoriesPanel from '../../components/service-categories/ServiceCategoriesPanel';
import ServicePackagesPanel from '../../components/service-packages/ServicePackagesPanel';
import CustomerServicePackagesPanel from '../../components/service-packages/CustomerServicePackagesPanel';
import ConsentFormsPanel from '../../components/consent-forms/ConsentFormsPanel';
import ServiceCombosPanel from '../../components/service-combos/ServiceCombosPanel';
import ConsentFormService from '../../services/consentFormService';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { Permissions } from '../../constants/Permissions';
import ServiceService from '../../services/serviceService';
import ProductService from '../../services/productService';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { getApiErrorCode } from '../../utils/apiErrorCode';
import {
  CATEGORIES_TAB,
  COMBOS_TAB,
  CONSENT_FORMS_TAB,
  CUSTOMER_PACKAGES_TAB,
  PACKAGES_TAB,
  SERVICES_TAB,
  resolveActiveTab,
} from './servicesPageTabs';

// The API rejects deposits it cannot honour; these say why in the owner's language.
const PREPAYMENT_ERROR_KEYS = {
  ServicePrepaymentConfigurationInvalid: 'servicesManagement.prepayment.errors.invalid',
  ServiceDepositBelowProcessingFee: 'servicesManagement.prepayment.errors.belowProcessingFee',
  ServicePrepaymentRequiresOnlinePayments: 'servicesManagement.prepayment.errors.onlinePaymentsRequired',
  ConsentFormNotFound: 'consentForms.apiErrors.ConsentFormNotFound',
  PlanUpgradeRequired: 'consentForms.apiErrors.PlanUpgradeRequired',
  FreeServiceConfigurationInvalid: 'servicesManagement.validation.freeServiceConfiguration',
};

const CLOSED_DIALOG_STATE = { open: false, mode: 'create', service: null };
const FALLBACK_LANGUAGES = ['en'];

const getAssignedTeamMemberIds = (teamMembers, serviceId) => (
  (teamMembers || [])
    .filter((member) => member.services?.some((assignedService) => assignedService.id === serviceId))
    .map((member) => member.id)
);

const sortTeamMembersByName = (teamMembers) => (
  [...(teamMembers || [])].sort((left, right) => (
    String(left.name || '').localeCompare(String(right.name || ''), undefined, { sensitivity: 'base' })
  ))
);

const ServicesPage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showError, showSuccess } = useToast();
  const { hasPermission } = usePermissions(storeId);
  const { isPro, entitlements, loading: planLoading } = useStorePlan(storeId);
  const canReadPackages = hasPermission(Permissions.ServicePackagesRead);
  const canWritePackages = hasPermission(Permissions.ServicePackagesWrite);
  const planPending = planLoading && !entitlements;
  const allowsServicePackages = Boolean(entitlements?.allowsServicePackages);
  const allowsServiceCombos = Boolean(entitlements?.allowsServiceCombos);
  const canReadConsentForms = hasPermission(Permissions.ConsentFormsRead);
  const canWriteConsentForms = hasPermission(Permissions.ConsentFormsWrite);
  const allowsConsentForms = Boolean(entitlements?.allowsConsentForms);
  const canCreateServices = hasPermission(Permissions.ServicesCreate);
  const activeTab = resolveActiveTab(
    searchParams.get('tab'),
    canReadPackages,
    canReadConsentForms,
    canCreateServices,
    allowsServicePackages,
    allowsConsentForms,
    allowsServiceCombos,
  );

  const [services, setServices] = useState([]);
  const catalogServices = useMemo(
    () => services.filter((service) => !service.isCombo),
    [services],
  );
  const [serviceContext, setServiceContext] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogState, setDialogState] = useState(CLOSED_DIALOG_STATE);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [savingService, setSavingService] = useState(false);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  const [consentForms, setConsentForms] = useState([]);
  const [catalogProducts, setCatalogProducts] = useState(null);

  const canEditServices = hasPermission(Permissions.ServicesUpdate);
  const canDeleteServices = hasPermission(Permissions.ServicesDelete);
  const canManageServices = canCreateServices || canEditServices || canDeleteServices;
  const canReadProducts = hasPermission(Permissions.ProductsRead);
  const showRecipe = Boolean(entitlements?.allowsProducts) && canReadProducts;

  const lockProFeatures = Boolean(entitlements) && !isPro;
  const tabs = useMemo(() => ([
    { value: SERVICES_TAB, label: t('servicesManagement.tabs.services') },
    { value: CATEGORIES_TAB, label: t('servicesManagement.tabs.categories') },
    ...(canCreateServices ? [{
      value: COMBOS_TAB,
      label: t('servicesManagement.tabs.combos'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canReadPackages ? [{
      value: PACKAGES_TAB,
      label: t('servicesManagement.tabs.packages'),
      proLocked: lockProFeatures,
    }, {
      value: CUSTOMER_PACKAGES_TAB,
      label: t('servicesManagement.tabs.customerPackages'),
      mobileLabel: t('servicesManagement.tabs.customerPackagesShort'),
      proLocked: lockProFeatures,
    }] : []),
    ...(canReadConsentForms ? [{
      value: CONSENT_FORMS_TAB,
      label: t('servicesManagement.tabs.consentForms'),
      proLocked: lockProFeatures,
    }] : []),
  ]), [canCreateServices, canReadConsentForms, canReadPackages, lockProFeatures, t]);

  // The service editor offers these forms; a store without Pro only keeps what it already set.
  const loadConsentForms = useCallback(async () => {
    if (!storeId || !canReadConsentForms) {
      setConsentForms([]);
      return;
    }

    try {
      setConsentForms(await ConsentFormService.getConsentForms(storeId));
    } catch (consentError) {
      console.error('Failed to load consent forms:', consentError);
      setConsentForms([]);
    }
  }, [canReadConsentForms, storeId]);

  useEffect(() => {
    loadConsentForms();
  }, [loadConsentForms]);

  useEffect(() => {
    if (!storeId || !showRecipe) {
      setCatalogProducts(null);
      return undefined;
    }

    let cancelled = false;
    requestDeduplicator.deduplicate(
      `service-recipe-products-${storeId}`,
      () => ProductService.getProductManagementData(storeId),
    ).then((data) => {
      if (!cancelled) setCatalogProducts(data.products || []);
    }).catch((productError) => {
      console.error('Failed to load products for service recipes:', productError);
      if (!cancelled) setCatalogProducts([]);
    });

    return () => {
      cancelled = true;
    };
  }, [showRecipe, storeId]);

  const loadServices = useCallback(async () => {
    if (!storeId) {
      setError(t('servicesManagement.error.noStoreSelected'));
      setLoading(false);
      return;
    }

    const requestKey = `getServiceManagement-${storeId}`;

    return requestDeduplicator.deduplicate(requestKey, async () => {
      try {
        setLoading(true);
        setError(null);

        const [data, teamMembersResult] = await Promise.all([
          ServiceService.getServiceManagementData(storeId),
          TeamMemberService.getTeamMembersByStore(storeId).catch(() => []),
        ]);
        setServiceContext(data);
        setServices(data.services);
        setTeamMembers(sortTeamMembersByName(teamMembersResult));
      } catch (loadError) {
        console.error('Failed to load services:', loadError);
        setError(t('servicesManagement.error.loading'));
      } finally {
        setLoading(false);
      }
    });
  }, [storeId, t]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleTabChange = (nextTab) => {
    if (nextTab === SERVICES_TAB) {
      setSearchParams({});
      return;
    }

    setSearchParams({ tab: nextTab });
  };

  const handleSaveService = async (service) => {
    try {
      setSavingService(true);

      if (dialogState.mode === 'edit' && dialogState.service?.id) {
        await ServiceService.updateService(storeId, dialogState.service.id, service);
      } else {
        await ServiceService.createService(storeId, service);
      }

      showSuccess(
        t(dialogState.mode === 'edit' ? 'servicesManagement.feedback.updated' : 'servicesManagement.feedback.created')
      );
      setDialogState(CLOSED_DIALOG_STATE);
      await loadServices();
    } catch (saveError) {
      console.error('Failed to save service:', saveError);
      const prepaymentErrorKey = PREPAYMENT_ERROR_KEYS[getApiErrorCode(saveError)];
      showError(t(prepaymentErrorKey
        || (dialogState.mode === 'edit' ? 'servicesManagement.error.update' : 'servicesManagement.error.create')));
    } finally {
      setSavingService(false);
    }
  };

  const handleDeleteService = async () => {
    const targetService = serviceToDelete;
    if (!targetService) {
      return;
    }

    try {
      setDeletingServiceId(targetService.id);
      await ServiceService.deleteService(storeId, targetService.id);
      showSuccess(t('servicesManagement.feedback.deleted'));
      await loadServices();
    } catch (deleteError) {
      console.error('Failed to delete service:', deleteError);
      const code = getApiErrorCode(deleteError);
      showError(t(code === 'ServiceBelongsToCombo'
        ? 'serviceCombosManagement.errors.ServiceBelongsToCombo'
        : 'servicesManagement.error.delete'));
    } finally {
      setDeletingServiceId(null);
      setServiceToDelete(null);
    }
  };

  if (loading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('servicesManagement.title')}
        pageSubtitle={t('servicesManagement.subtitle')}
        variant="cards"
        withAction={canManageServices}
      />
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadServices}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <ManagementPageLayout
      id="services-page"
      containerId="services-header"
      titleId="services-title"
      subtitleId="services-subtitle"
      pageTitle={t('servicesManagement.title')}
      pageSubtitle={t('servicesManagement.subtitle')}
      contentId="services-main-content"
    >
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={handleTabChange}
        contentId="services-tabs-content"
        equalWidthOnMobile
      >
        <Box role="tabpanel" hidden={activeTab !== SERVICES_TAB} id="services-tabpanel-services">
          {activeTab === SERVICES_TAB ? (
            <Box id="services-content" sx={{ p: 4 }}>
              {canCreateServices ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4, pt: 1 }}>
                  <PrimaryActionButton
                    startIcon={<AddIcon />}
                    onClick={() => setDialogState({ open: true, mode: 'create', service: { teamMemberIds: [] } })}
                    sx={{
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    {t('servicesManagement.addNew')}
                  </PrimaryActionButton>
                </Box>
              ) : null}

              {catalogServices.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
                    {t('servicesManagement.noServices')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('servicesManagement.emptyHint')}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3} p={1} sx={{ pt: 2 }}>
                  {catalogServices.map((service) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={service.id}>
                      <ServiceCard
                        service={service}
                        currency={serviceContext?.currency}
                        loading={savingService || deletingServiceId === service.id}
                        canEdit={canEditServices}
                        canDelete={canDeleteServices}
                        showPrepayment={isPro}
                        onEdit={() => setDialogState({
                          open: true,
                          mode: 'edit',
                          service: {
                            ...service,
                            teamMemberIds: getAssignedTeamMemberIds(teamMembers, service.id),
                          },
                        })}
                        onDelete={() => setServiceToDelete(service)}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          ) : null}
        </Box>

        <Box role="tabpanel" hidden={activeTab !== CATEGORIES_TAB} id="services-tabpanel-categories">
          {activeTab === CATEGORIES_TAB ? (
            <ServiceCategoriesPanel
              storeId={storeId}
              canCreate={canCreateServices}
              canEdit={canEditServices}
              canDelete={canDeleteServices}
            />
          ) : null}
        </Box>

        {canCreateServices ? (
          <Box role="tabpanel" hidden={activeTab !== COMBOS_TAB} id="services-tabpanel-combos">
            {activeTab === COMBOS_TAB ? (
              allowsServiceCombos ? (
                <ServiceCombosPanel
                  storeId={storeId}
                  services={catalogServices}
                  supportedLanguages={serviceContext?.supportedLanguages?.length ? serviceContext.supportedLanguages : FALLBACK_LANGUAGES}
                  currency={serviceContext?.currency || 'EUR'}
                  canWrite={canCreateServices}
                  isPro={isPro}
                  onlinePaymentsReady={Boolean(serviceContext?.stripePaymentReady)
                    && allowsOnline(serviceContext?.appointmentCustomerPaymentMode)}
                  installmentMethodsAvailable={Boolean(serviceContext?.installmentMethodsAvailable)}
                />
              ) : planPending ? (
                <ManagementContentSkeleton variant="cards" withAction={canCreateServices} />
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Alert id="service-combos-upgrade-alert" severity="info">
                    {t('storePlan.upgradeRequired')}
                  </Alert>
                </Box>
              )
            ) : null}
          </Box>
        ) : null}

        {canReadPackages ? (
          <Box role="tabpanel" hidden={activeTab !== PACKAGES_TAB} id="services-tabpanel-packages">
            {activeTab === PACKAGES_TAB ? (
              allowsServicePackages ? (
                <ServicePackagesPanel storeId={storeId} canWrite={canWritePackages} />
              ) : planPending ? (
                <ManagementContentSkeleton variant="cards" withAction={canWritePackages} />
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Alert id="service-packages-upgrade-alert" severity="info">
                    {t('storePlan.upgradeRequired')}
                  </Alert>
                </Box>
              )
            ) : null}
          </Box>
        ) : null}

        {canReadPackages ? (
          <Box role="tabpanel" hidden={activeTab !== CUSTOMER_PACKAGES_TAB} id="services-tabpanel-customer-packages">
            {activeTab === CUSTOMER_PACKAGES_TAB ? (
              allowsServicePackages ? (
                <CustomerServicePackagesPanel storeId={storeId} />
              ) : planPending ? (
                <ManagementContentSkeleton variant="detail" />
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Alert id="customer-service-packages-upgrade-alert" severity="info">
                    {t('storePlan.upgradeRequired')}
                  </Alert>
                </Box>
              )
            ) : null}
          </Box>
        ) : null}

        {canReadConsentForms ? (
          <Box role="tabpanel" hidden={activeTab !== CONSENT_FORMS_TAB} id="services-tabpanel-consent-forms">
            {activeTab === CONSENT_FORMS_TAB ? (
              allowsConsentForms ? (
                <ConsentFormsPanel
                  storeId={storeId}
                  canWrite={canWriteConsentForms}
                  supportedLanguages={serviceContext?.supportedLanguages?.length ? serviceContext.supportedLanguages : FALLBACK_LANGUAGES}
                  onChanged={loadConsentForms}
                />
              ) : planPending ? (
                <ManagementContentSkeleton variant="cards" withAction={canWriteConsentForms} />
              ) : (
                <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                  <Alert id="consent-forms-upgrade-alert" severity="info">
                    {t('storePlan.upgradeRequired')}
                  </Alert>
                </Box>
              )
            ) : null}
          </Box>
        ) : null}
      </FolderTabs>

      <ServiceEditorDialog
        open={dialogState.open}
        mode={dialogState.mode}
        service={dialogState.service}
        teamMembers={teamMembers}
        supportedLanguages={serviceContext?.supportedLanguages?.length ? serviceContext.supportedLanguages : FALLBACK_LANGUAGES}
        currency={serviceContext?.currency || 'EUR'}
        onlinePaymentsReady={Boolean(serviceContext?.stripePaymentReady)
          && allowsOnline(serviceContext?.appointmentCustomerPaymentMode)}
        installmentMethodsAvailable={Boolean(serviceContext?.installmentMethodsAvailable)}
        consentForms={consentForms}
        consentFormsEnabled={allowsConsentForms && canReadConsentForms}
        catalogProducts={showRecipe ? (catalogProducts || []) : undefined}
        canSeeCosts={hasPermission(Permissions.StockCostsRead)}
        saving={savingService}
        onClose={() => setDialogState(CLOSED_DIALOG_STATE)}
        onSave={handleSaveService}
      />

      <ConfirmDialog
        open={Boolean(serviceToDelete)}
        onClose={() => setServiceToDelete(null)}
        onConfirm={handleDeleteService}
        title={t('servicesManagement.delete.title')}
        message={t('servicesManagement.delete.message', {
          name: serviceToDelete ? Object.values(serviceToDelete.name || {}).find((value) => value?.trim()) || '' : '',
        })}
        confirmText={t('servicesManagement.delete.confirm')}
        cancelText={t('servicesManagement.delete.cancel')}
      />
    </ManagementPageLayout>
  );
};

export default ServicesPage;
